import { useState, type ReactElement } from 'react';

import { Button } from '@/components/ui/button';
import { ChatProvider, ChatWindow, type ColorMode } from '@/features/chat';

const apiBaseUrl = (import.meta.env.VITE_API_URL ?? '').trim().replace(/\/+$/, '');

export function DevApp(): ReactElement {
  const [mode, setMode] = useState<'floating' | 'fullscreen'>('fullscreen');
  const [colorMode, setColorMode] = useState<ColorMode>('system');

  return (
    <ChatProvider colorMode={colorMode} config={{ apiBaseUrl }}>
      <main className="bg-background text-foreground flex min-h-dvh flex-col items-center justify-center gap-6 p-6 font-sans">
        <header className="text-center">
          <h1 className="text-2xl font-semibold">seplag-xbot-frontend · dev playground</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Set <code>VITE_API_URL</code> in <code>.env.local</code> to point at the backend.
          </p>
        </header>

        <section className="bg-card text-card-foreground flex flex-col items-center gap-3 rounded-2xl border p-6 shadow-sm">
          <p className="text-muted-foreground text-xs tracking-wide uppercase">Layout mode</p>
          <div className="flex gap-2">
            <Button
              variant={mode === 'floating' ? 'default' : 'outline'}
              onClick={() => {
                setMode('floating');
              }}
            >
              Floating
            </Button>
            <Button
              variant={mode === 'fullscreen' ? 'default' : 'outline'}
              onClick={() => {
                setMode('fullscreen');
              }}
            >
              Fullscreen
            </Button>
          </div>

          <p className="text-muted-foreground mt-3 text-xs tracking-wide uppercase">Color mode</p>
          <div className="flex gap-2">
            {(['system', 'light', 'dark'] as const).map((m) => (
              <Button
                key={m}
                variant={colorMode === m ? 'default' : 'outline'}
                onClick={() => {
                  setColorMode(m);
                }}
              >
                {m}
              </Button>
            ))}
          </div>

          <p className="text-muted-foreground max-w-sm text-center text-xs">
            On screens narrower than 768px the chat collapses to a bottom Drawer regardless of the
            layout mode chosen here.
          </p>
        </section>
      </main>

      <ChatWindow mode={mode} />
    </ChatProvider>
  );
}
